/**
 * PlatformBreakdown — per-platform AI citation rate table with tier gating.
 * Sprint 2.8c Phase 4. Spec §C.1.
 *
 * Data comes from nowAudit.geoSweep.platforms — server already tier-redacts
 * the entries: active rows carry full data; locked rows carry { locked: true,
 * requiredTier }. No client-side tier logic needed.
 *
 * Locked rows use Pattern B (clean lock state — no blurred fake numbers).
 * Locked-row click → api.billing.checkout(requiredTier) → Stripe Checkout.
 * TODO(2.8d): Replace with polished /billing/upgrade flow once Sprint 2.8d ships.
 *
 * Layout:
 *   Desktop ≥ 640px: accessible <table> with thead/tbody/th[scope]
 *   Mobile  < 640px: vertical card list
 */

import { useState } from 'react';
import { api } from '../../api';
import { t } from '../../i18n';

// ---------------------------------------------------------------------------
// Static maps
// ---------------------------------------------------------------------------

const PLATFORM_DISPLAY: Record<string, string> = {
  chatgpt:             'ChatGPT',
  google_ai_overviews: 'Google AI Overviews',
  claude:              'Claude',
  perplexity:          'Perplexity',
  gemini:              'Gemini',
  mistral:             'Mistral',
  deepseek:            'DeepSeek',
  copilot:             'Microsoft Copilot',
};

const TIER_DISPLAY: Record<string, string> = {
  free: 'Free', tier_1: 'Starter', tier_2: 'Growth', tier_3: 'Scale',
  starter: 'Starter', growth: 'Growth', scale: 'Scale', enterprise: 'Enterprise',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActivePlatform = {
  platform: string;
  locked: false;
  citationRate: number;
  citationCount: number;
  queriesRun: number;
};

type LockedPlatform = {
  platform: string;
  locked: true;
  requiredTier: string;
};

type PlatformEntry = ActivePlatform | LockedPlatform;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platforms: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thenPlatforms: any[];
  customerTier: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTrendArrow(thenRate: number | undefined, nowRate: number): string {
  if (thenRate === undefined) return '';
  const diff = nowRate - thenRate;
  if (diff > 0.005) return '↑';
  if (diff < -0.005) return '↓';
  return '·';
}

function getTrendColor(thenRate: number | undefined, nowRate: number): string {
  if (thenRate === undefined) return 'text-slate-400';
  const diff = nowRate - thenRate;
  if (diff > 0.005) return 'text-emerald-600';
  if (diff < -0.005) return 'text-rose-600';
  return 'text-slate-400';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlatformBreakdown({ platforms, thenPlatforms, customerTier }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const accordionId = 'platform-breakdown-body';

  const currentTierName = TIER_DISPLAY[customerTier] ?? (customerTier.charAt(0).toUpperCase() + customerTier.slice(1));

  async function handleUpgradeCta(requiredTier: string) {
    if (checkingOut) return;
    setCheckingOut(true);
    try {
      const SLUG_MAP: Record<string, 'tier_1' | 'tier_2' | 'tier_3'> = {
        starter: 'tier_1', tier_1: 'tier_1',
        growth: 'tier_2', tier_2: 'tier_2',
        scale: 'tier_3', tier_3: 'tier_3',
      };
      const { url } = await api.billing.checkout(SLUG_MAP[requiredTier] ?? 'tier_1');
      window.location.href = url;
    } catch {
      setCheckingOut(false);
    }
  }

  // Normalise server-shaped platform entries
  const entries: PlatformEntry[] = (platforms ?? []).map((p) => {
    if (p.locked) {
      return { platform: p.platform, locked: true, requiredTier: p.requiredTier ?? 'growth' };
    }
    return {
      platform:     p.platform,
      locked:       false,
      citationRate: typeof p.citationRate === 'number' ? p.citationRate : 0,
      citationCount: typeof p.citationCount === 'number' ? p.citationCount : 0,
      queriesRun:   typeof p.queriesRun === 'number' ? p.queriesRun : 0,
    };
  });

  // Sort: active first (citation rate desc), locked last
  const sorted = [...entries].sort((a, b) => {
    if (a.locked && !b.locked) return 1;
    if (!a.locked && b.locked) return -1;
    if (!a.locked && !b.locked) return b.citationRate - a.citationRate;
    return 0;
  });

  // Build then-rate lookup for trend arrows
  const thenByPlatform: Record<string, number> = {};
  for (const p of thenPlatforms ?? []) {
    if (!p.locked && typeof p.citationRate === 'number') {
      thenByPlatform[p.platform] = p.citationRate;
    }
  }

  const activeCount = entries.filter((e) => !e.locked).length;

  if (entries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-4">
      {/* Toggle button */}
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={accordionId}
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">
            {t('platform_breakdown.title', 'Platform breakdown')}
          </span>
          <span className="text-xs text-slate-400">
            ({activeCount} of {entries.length} active)
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Body */}
      <div
        id={accordionId}
        className={`overflow-hidden transition-all duration-200 ease-out ${expanded ? 'max-h-[1200px]' : 'max-h-0'}`}
      >
        {/* ── Desktop table (≥ 640px) ── */}
        <div className="hidden sm:block">
          <table
            className="w-full text-sm border-collapse"
            aria-label={t('platform_breakdown.table_label', 'AI platform citation rate breakdown')}
          >
            <thead>
              <tr className="border-b border-slate-100">
                <th
                  scope="col"
                  className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400"
                >
                  {t('platform_breakdown.col_platform', 'Platform')}
                </th>
                <th
                  scope="col"
                  className="px-5 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-400"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {t('platform_breakdown.col_citation_rate', 'Citation rate')}
                </th>
                <th
                  scope="col"
                  className="px-5 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-400"
                >
                  {t('platform_breakdown.col_trend', 'Trend')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((entry) => {
                const displayName = PLATFORM_DISPLAY[entry.platform] ?? entry.platform;

                if (entry.locked) {
                  const tierName = TIER_DISPLAY[entry.requiredTier] ?? entry.requiredTier;
                  return (
                    <tr key={entry.platform} className="border-b border-slate-50 last:border-0">
                      <td className="px-5 py-3">
                        <span className="text-slate-400">{displayName}</span>
                      </td>
                      <td className="px-5 py-3 text-right" colSpan={2}>
                        {/* TODO(2.8d): Replace this redirect with the polished /billing/upgrade flow once Sprint 2.8d ships.
                            Current behaviour: calls api.billing.checkout(requiredTier) → redirects to Stripe Checkout for the target plan.
                            See SPRINT_2_8c_PATCH_PROMPT.md for context. */}
                        <button
                          type="button"
                          disabled={checkingOut}
                          onClick={() => void handleUpgradeCta(entry.requiredTier)}
                          aria-label={`${displayName} is not available on ${currentTierName} tier. Click to upgrade to ${tierName}.`}
                          className="text-slate-600 hover:text-slate-700 text-xs inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                        >
                          {t('progress.platform_breakdown.locked_row_cta', 'This is not available on {currentTier} — upgrade to {requiredTier} →', { currentTier: currentTierName, requiredTier: tierName })}
                        </button>
                      </td>
                    </tr>
                  );
                }

                const thenRate = thenByPlatform[entry.platform];
                const arrow = getTrendArrow(thenRate, entry.citationRate);
                const arrowColor = getTrendColor(thenRate, entry.citationRate);

                return (
                  <tr key={entry.platform} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-3">
                      <span className="text-slate-700 font-medium">{displayName}</span>
                    </td>
                    <td
                      className="px-5 py-3 text-right"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      <span className="text-slate-700 font-medium">
                        {(entry.citationRate * 100).toFixed(1)}%
                      </span>
                      {entry.queriesRun > 0 && (
                        <span className="ml-1.5 text-slate-400 text-xs">
                          ({entry.citationCount} of {entry.queriesRun})
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-5 py-3 text-right font-semibold ${arrowColor}`}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                      aria-label={arrow === '↑' ? 'Improved' : arrow === '↓' ? 'Declined' : 'Unchanged'}
                    >
                      {arrow}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Mobile cards (< 640px) ── */}
        <div className="sm:hidden divide-y divide-slate-100">
          {sorted.map((entry) => {
            const displayName = PLATFORM_DISPLAY[entry.platform] ?? entry.platform;

            if (entry.locked) {
              const tierName = TIER_DISPLAY[entry.requiredTier] ?? entry.requiredTier;
              return (
                <div key={entry.platform} className="px-5 py-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-400 shrink-0">{displayName}</span>
                  {/* TODO(2.8d): Replace this redirect with the polished /billing/upgrade flow once Sprint 2.8d ships.
                      Current behaviour: calls api.billing.checkout(requiredTier) → redirects to Stripe Checkout for the target plan.
                      See SPRINT_2_8c_PATCH_PROMPT.md for context. */}
                  <button
                    type="button"
                    disabled={checkingOut}
                    onClick={() => void handleUpgradeCta(entry.requiredTier)}
                    aria-label={`${displayName} is not available on ${currentTierName} tier. Click to upgrade to ${tierName}.`}
                    className="text-xs text-slate-600 hover:text-slate-700 transition-colors disabled:opacity-50 text-right"
                  >
                    {t('progress.platform_breakdown.locked_row_cta', 'This is not available on {currentTier} — upgrade to {requiredTier} →', { currentTier: currentTierName, requiredTier: tierName })}
                  </button>
                </div>
              );
            }

            const thenRate = thenByPlatform[entry.platform];
            const arrow = getTrendArrow(thenRate, entry.citationRate);
            const arrowColor = getTrendColor(thenRate, entry.citationRate);

            return (
              <div key={entry.platform} className="px-5 py-3 flex items-center justify-between">
                <span className="text-sm text-slate-700 font-medium">{displayName}</span>
                <div
                  className="flex items-center gap-2"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <span className="text-sm text-slate-700 font-medium">
                    {(entry.citationRate * 100).toFixed(1)}%
                  </span>
                  {arrow && (
                    <span
                      className={`text-sm font-semibold ${arrowColor}`}
                      aria-label={arrow === '↑' ? 'Improved' : arrow === '↓' ? 'Declined' : 'Unchanged'}
                    >
                      {arrow}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <p className="text-xs text-slate-400">
            {t('platform_breakdown.footer', 'Rates based on the latest GeoSweep audit.')}{' '}
            <a
              href="/glossary#geosweep"
              className="text-emerald-700 hover:text-emerald-600 font-medium"
            >
              How we measure →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
